import type { DataItemUidType } from '@aytm/app/insights/dashboard/models/types.ServerResponse'
import type {
  AttributeType,
  MetricsValue,
  SubjectEditorData,
  SubjectType,
  TrackTargetType,
  ItemType,
  AttributeRow,
  ColorsModel,
  SubjectEditor
} from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import { getCurrentBlock, updateState } from './requests'
import { captionsFactory } from './attributes'
import { cellDataFactory } from './values'
import { defaultEditor } from '@aytm/app/insights/dashboard/lib/config'

type WaveEntry = {
  index: number
  label: string
}

type AttributeEntry = {
  key?: string
  method: string
  target: 'attribute' | 'entity'
  label: string
}

type AggregatedMetrics = {
  type: string
  value: unknown
}

type AggregatedValue = {
  type: string
  method: string
  value: unknown
  metrics?: Record<string, AggregatedMetrics>
}

type SubjectDataMapperResultType = {
  uid: string
  type: string
  waveIndex: number
  text: string
  image: object
  nativeUid?: string
  hash?: string
  waves?: Record<DataItemUidType, WaveEntry>
  attributes?: Record<string, AttributeEntry>
  aggregatedValues?: {
    values: Record<string, AggregatedValue>
  }
}

type SubjectDataMapperMethod = (subjectData: SubjectEditorData) => SubjectDataMapperResultType

const correctValue = (value: unknown, type: string): string | string[] | number => {
  switch (type) {
    case 'date': {
      return new Date(value as number).toISOString()
    }
    case 'tags': {
      return value ? (value as string[]) : ([] as string[])
    }
    default: {
      return undefined === value || null === value ? null : isNaN(Number(value)) ? null : Number(value)
    }
  }
}

const correctMetrics = (metrics?: Record<string, MetricsValue>) =>
  metrics
    ? Object.entries(metrics).reduce(
        (acc, [method, data]) => {
          acc[method] = {
            ...data,
            value: data.value ? correctValue(data.value, data.type) : data.value
          } as MetricsValue
          return acc
        },
        {} as Record<string, MetricsValue>
      )
    : metrics

const isEdit = (attribute: AttributeType): boolean => ['percent', 'date', 'number'].includes(attribute?.type)

export const subjectDataMapperFactory =
  (attributes: Record<string, AttributeType>, trackTarget: TrackTargetType): SubjectDataMapperMethod =>
  subjectData => {
    // Initial data
    const result: SubjectDataMapperResultType = {
      uid: subjectData.uid,
      nativeUid: subjectData.nativeUid,
      type: subjectData.type,
      waveIndex: subjectData.waveIndex,
      text: subjectData.text || '',
      image: subjectData.image || null
    }
    // Process values
    if (subjectData.values) {
      // Collect attributes
      result.attributes = Object.keys(subjectData.values)
        .filter(key => attributes[key])
        .reduce(
          (acc, key) => {
            const target = 'attribute' === trackTarget ? 'entity' : 'attribute'
            const label = attributes[key]?.text || ''
            const method = subjectData.values[key]?.method || ''
            acc[key] = { label, method, target }
            return acc
          },
          {} as Record<string, AttributeEntry>
        )
      // Collect values
      result.aggregatedValues = {
        values: Object.entries(subjectData.values)
          .filter(([attribute]) => attributes[attribute])
          .reduce(
            (acc, [attribute, data]) => {
              const { value, method, type, metrics } = data
              acc[attribute] = {
                value: correctValue(value, type),
                metrics: correctMetrics(metrics),
                method,
                type
              } as AggregatedValue
              return acc
            },
            {} as Record<string, AggregatedValue>
          )
      }
    }
    return result
  }

const getProps = (subject: SubjectType, subjectType?: string, subjectHash?: string): SubjectDataMapperResultType => {
  return {
    uid: subject?.uid || null,
    nativeUid: subject?.nativeUid || null,
    type: subjectType || subject?.type || null,
    image: subject?.image ? Object.assign({}, subject.image) : null,
    text: subject?.text || '',
    waveIndex: subject?.waveIndex || null,
    hash: subjectHash || null
  }
}

export const getSubjectProps = (state: InsightsDashboardPoolState, item?: ItemType) => {
  // Get state properties
  const block = getCurrentBlock(state)
  const subjects = state.currentData?.subjects || {}
  const subjectType = block?.getSubjectType() || null
  const subjectUid = item?.uid
  const subject = subjects?.[subjectUid]
  const data = state?.currentData?.data?.[subjectUid || ''] || null
  const subjectHash = 'string' === typeof data ? data : data?.hash || null
  return getProps(subject, subjectType, subjectHash)
}

export const getSubjectData = (state: InsightsDashboardPoolState, item?: ItemType) => {
  // Get state properties
  const block = getCurrentBlock(state)
  const items = block?.getBlockItems()
  const data = state.currentData?.data || {}
  const subjects = state.currentData?.subjects || {}
  const attributes = state.currentData?.attributes || {}
  const dataMetrics = state.currentData?.dataMetrics || {}
  const sections = block?.getSections()
  const trackTarget = block?.getObject('trackTarget') || 'entity'
  const stat = state.currentData?.stat || {}
  const sortByAttribute = block?.get<string>('sortByAttribute') || null
  const colors = block?.get<ColorsModel>('colors') || null
  const currentSplit = block?.get<string>('currentSplit') || null
  const showSigtest = block?.get<boolean>('showSigtest') || false
  const subjectType = block?.getSubjectType() || null
  // Create getters
  const getCaptions = captionsFactory({ attributes, dataMetrics, sections, trackTarget })
  const getCellData = cellDataFactory({
    items,
    data,
    stat,
    subjects,
    attributes,
    sortByAttribute,
    showSigtest,
    colors,
    currentSplit
  })
  // Collect subject aggregated data values
  const subjectUid = item?.uid
  const subject = subjects?.[subjectUid]
  const subjectProps = getProps(subject, subjectType)
  const captions = getCaptions({ showSections: false, showUnique: true })
  const dataValues = captions.reduce(
    (acc, cur) => {
      const row = cur as AttributeRow
      const attribute = attributes[row.attribute]
      const type = row.metrics?.type || row.defaultMetrics?.type || attribute?.type
      if (isEdit(attribute)) {
        const cellData = getCellData(item, row.attribute, row.metrics, row.defaultMetrics)
        if (!acc[row.attribute]) {
          acc[row.attribute] = {
            type,
            value: cellData.value,
            method: row.metrics?.id || row.defaultMetrics?.id || 'percent'
          }
        }
        if (row.metrics) {
          if (!acc[row.attribute].metrics) {
            acc[row.attribute].metrics = {}
          }
          acc[row.attribute].metrics[row.metrics.id] = {
            type,
            value: cellData.value
          }
        }
      }
      return acc
    },
    {} as Record<string, AggregatedValue>
  )
  return {
    ...subjectProps,
    values: dataValues
  }
}

export const mapSubjectData = (
  state: InsightsDashboardPoolState,
  subjectData: SubjectEditorData
): SubjectDataMapperResultType => {
  const block = getCurrentBlock(state)
  const attributes = state.currentData?.attributes || {}
  const trackTarget = block?.getObject('trackTarget') || 'entity'
  return subjectDataMapperFactory(attributes, trackTarget)(subjectData)
}

export const updateEditor = (
  state: InsightsDashboardPoolState,
  update?: Partial<SubjectEditor>
): InsightsDashboardPoolState => {
  const editor = state.editor
  const newEditor = update ? { ...editor, ...update } : defaultEditor
  return updateState(state, { editor: newEditor })
}

export const updateEditorError = (
  state: InsightsDashboardPoolState,
  { type, message }: { type: string; message: string[] | string }
) => {
  let resultState = state
  const editor = state.editor
  if (editor && type) {
    const errors = editor.errors || {}
    const messages = message ? (Array.isArray(message) ? message : [message]) : null
    if (messages?.length) {
      const oldMessages = errors[type]?.filter(msg => !messages.includes(msg)) || []
      const newMessages = [...oldMessages, ...messages]
      resultState = updateEditor(state, { errors: { ...errors, [type]: newMessages } })
    } else {
      const { [type]: _, ...newErrors } = errors
      resultState = updateEditor(state, { errors: newErrors })
    }
  } else {
    resultState = updateEditor(state, { errors: {} })
  }
  return resultState
}
